import dayjs from "dayjs";

import { FR_CONTRACT_TYPE } from "constants/haulpay-broker";
import { routes } from "constants/routes";
import { IOption } from "models/Form";
import { IPaymentSpeed } from "models/HaulPay";
import { IActivityItem } from "models/Home";
import { ILocations, IQueryLoadData, LoadSearchFilterFormData } from "models/Load";

export function getFormData(object: any) {
  const formData = new FormData();
  Object.keys(object).forEach((key) => formData.append(key, object[key]));
  return formData;
}

export function convertMsToRemainingTime(ms: number) {
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  return days > 0
    ? `${days} Days ${hours}:${minutes}:${seconds}`
    : `${hours}:${minutes}:${seconds}`;
}

export function convertDiffToAge(ms: number) {
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  if (days) return days + "d";
  if (hours) return hours + "h";
  if (minutes) return minutes + "m";
  if (seconds) return seconds + "s";
  return "-";
}

export const convertToFullAddress = (
  address: string,
  city: string,
  state: string,
  country: string
) => {
  const streetStr = [address, city, state, country]
    .filter((item) => !!item && item !== "undefined")
    .join(", ");
  return streetStr ?? "Anywhere";
};

export const convertTime = (time: string) => {
  return {
    formatted: dayjs(time).format("DD MMM YYYY"),
    day: dayjs(time).format("DD MMM"),
    time: dayjs(time).format("hh:mm A")
  };
};

export const convertTruckData = (data: any[]) => {
  return data.map((item: any, index) => ({
    ...item,
    timeStart: {
      timeStart: dayjs(item.timeStart).format("DD MMM YYYY"),
      startDay: dayjs(item.timeStart).format("DD MMM"),
      startTime: dayjs(item.timeStart).format("hh:mm A")
    },
    timeEnd: {
      timeEnd: dayjs(item.timeEnd).format("DD MMM YYYY"),
      endDay: dayjs(item.timeEnd).format("DD MMM"),
      endTime: dayjs(item.timeEnd).format("hh:mm A")
    },
    index
  }));
};

export const convertSavedLoadData = (data: any[]) => {
  return data.map(
    (item: any, index) =>
      ({
        ...item,
        timeStart: {
          timeStart: item.timeStart,
          startDay: dayjs(item.timeStart).format("DD MMM"),
          startTime: dayjs(item.timeStart).format("hh:mm A")
        },
        timeEnd: {
          timeEnd: item.timeEnd,
          endDay: dayjs(item.timeEnd).format("DD MMM"),
          endTime: dayjs(item.timeEnd).format("hh:mm A")
        },
        timeDelivery: {
          timeDelivery: item.timeDelivery ?? "",
          deliveryDay: item.timeDelivery ? dayjs(item.timeDelivery).format("DD MMM") : "",
          deliveryTime: item.timeDelivery ? dayjs(item.timeDelivery).format("hh:mm A") : ""
        },
        index
      }) as IQueryLoadData
  );
};

export const convertLoadData = (data: any) => {
  return {
    ...data,
    timeStart: {
      timeStart: data.timeStart,
      startDay: dayjs(data.timeStart).format("DD MMM"),
      startTime: dayjs(data.timeStart).format("hh:mm A")
    },
    timeEnd: {
      timeEnd: data.timeEnd,
      endDay: dayjs(data.timeEnd).format("DD MMM"),
      endTime: dayjs(data.timeEnd).format("hh:mm A")
    },
    timeDelivery: {
      timeDelivery: data.timeDelivery ?? "",
      deliveryDay: data.timeDelivery ? dayjs(data.timeDelivery).format("DD MMM") : "",
      deliveryTime: data.timeDelivery ? dayjs(data.timeDelivery).format("hh:mm A") : ""
    }
  };
};

export const covertFrLocation = (location: ILocations) => {
  const { name, street_one, street_two, city, state, zip, country } = location;

  const locationName = name ? name + ":" : "";
  const locationAddress = [street_one, street_two, city, state, zip, country]
    .filter((item) => !!item && item !== undefined)
    .join(", ");

  return locationName + locationAddress;
};

export const convertLoadFilterFormData = (
  data: LoadSearchFilterFormData,
  tabValue: IOption,
  isDateRange: boolean
) => {
  const {
    originState,
    originRegion,
    originRadius,
    destinationState,
    destinationRegion,
    destinationRadius,
    equipmentType,
    weight,
    length,
    startDate,
    endDate
  } = data;

  let origin;

  if (originRegion?.length) {
    origin = originRegion[0]?.value;
  } else if (originState) {
    origin = originState?.state;
    origin = originState?.state;
  } else {
    origin = "";
  }

  let formData: any =
    tabValue.value === "by_city"
      ? {
          originCity: originState?.city,
          originState: origin,
          originRadius: Number(originRadius?.value),
          deliveryCity: destinationState?.city,
          destinationState: destinationState?.state,
          destinationRadius: Number(destinationRadius?.value),
          equipmentType: equipmentType?.length ? equipmentType.map(({ value }) => value) : [],
          length: length?.value,
          weight: weight
        }
      : {
          originState: originRegion?.length ? originRegion[0].value : "",
          originRadius: 200,
          destinationState: destinationRegion?.length ? destinationRegion[0].value : "",
          destinationRadius: 200,
          equipmentType: equipmentType?.length ? equipmentType.map(({ value }) => value) : [],
          length: length?.value,
          weight: weight
        };

  if (isDateRange) {
    formData = {
      ...formData,
      timeStart: startDate ? dayjs(startDate).format("YYYY-MM-DDTHH:mm:ss") : null,
      timeEnd: endDate ? dayjs(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss") : null
    };
  } else {
    formData = {
      ...formData,
      timeStart: startDate ? dayjs(startDate).format("YYYY-MM-DDTHH:mm:ss") : null,
      timeEnd: endDate ? dayjs(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss") : null
    };
  }

  return formData;
};

export const getPath = (data: IActivityItem, account: any) => {
  if (data.debtor_id) {
    return routes.HAULPAY_CREDIT_CHECK_CREDIT;
  } else if (data.bid_id && data.load_id) {
    return `${routes.LOADS_SEARCH}/${data.load_id}`;
  } else if (data.load_id) {
    if (data.funding_request_id) {
      return routes.HAULPAY_ASSIGNED_LOADS_PAYMENT_DETAILS + "/" + data.funding_request_id;
    } else {
      return data.user_id === account.id
        ? `${routes.LOADS_POST}/${data.load_id}`
        : `${routes.LOADS_SEARCH}/${data.load_id}`;
    }
  } else if (data.truck_id) {
    return data.user_id === account.id
      ? `${routes.TRUCKS_SEARCH}/${data.truck_id}`
      : `${routes.TRUCKS_MY_POSTED}/${data.truck_id}`;
  } else if (data.factoring_id) {
    return (data.funding_request_id as any)
      ? `${routes.HAULPAY_CARRIER_MANGE_PAY_PAYMENT_DETAILS}/${data.funding_request_id}`
      : routes.HAULPAY_CREDIT_CHECK_CREDIT;
  } else if (
    data.alert_type === "factoring_application_pending" ||
    data.alert_type === "factoring_welcome"
  ) {
    return routes.HAULPAY_STATUS;
  }

  return "";
};

export const frPermission = (frType?: IOption) => {
  const isNonFactored =
    frType?.value !== FR_CONTRACT_TYPE[1].value && frType?.value !== FR_CONTRACT_TYPE[2].value;
  const isSelfFinance =
    frType?.value === FR_CONTRACT_TYPE[3].value || frType?.value === FR_CONTRACT_TYPE[4].value;

  return {
    isNonFactored,
    isSelfFinance
  };
};

export const convertPaymentSpeedOption = (paymentSpeed: IPaymentSpeed): IOption => ({
  id: paymentSpeed?.payout_days,
  // label: `${paymentSpeed.in_process ? "Processing request for " : ""} ${paymentSpeed.name} (${paymentSpeed.carrier_rate}% Quick Pay Fee)`,
  label: `${paymentSpeed?.name} (${paymentSpeed?.carrier_rate}% Quick Pay Fee)`,
  value: `ach_${paymentSpeed?.payout_days === 0 ? 1 : paymentSpeed?.payout_days}_days`
});

export const parseError = (error: string): string => {
  // Extract the message within `Errors: { ... }`
  const match = error.match(/Errors: \{(.+?)\}/);
  if (match) {
    const errorContent = match[1];
    // Match all error entries within the Errors object, handling both single and double quotes around the string
    const matches = [
      ...errorContent.matchAll(/'(.+?)': \[ErrorDetail\(string=(["'])(.+?)\2, code='.+?'\)\]/g)
    ];

    // Extract all messages and join them as a single string
    const messages = matches.map(([, , , message]) => message);
    return messages.join("; "); // Combine messages into a single string
  }
  return error;
};
